import React, { useContext, useEffect, useRef, useState } from 'react'
import { AdmissionBoardApprovalFormData } from '../data/AdmissionBoardApprovalForm.Data';
import FormCreator from '../../../components/forms/FormCreator';
import { useSendAdmissionBoardApprovalMutation, useSubmitAdmissionBoardApprovalMutation } from '../../../app/services/AdmissionBoardApproval/AdmissionBoardServiceApi';
import { useParams } from 'react-router-dom';
import { decryptAESID } from '../../../utils/Encryption';
import SwalAlert from '../../../services/swalService/SwalService';
import MembershipTaskListContext from '../MembershipTaskListContextProvider';
import { setAdmissionCommitteeInitials } from '../utils/EditTaskListSetFormFields';
import CenterModel from '../../../components/ui/centerModel/CenterModel';
import DataLoader from '../../../components/FinalMolGrid/ui/dataLoader/DataLoader';
import { ApplicationStatus } from '../../../utils/Constants';
import { getAuditContext } from '../../../lib/authenticationLibrary';

const AdmissionBoardApprovalForm = () => {
    const admissionCommitteRef = useRef();
    const [admissionCommitteForm, setAdmissionCommitteFormForm] = useState(AdmissionBoardApprovalFormData);
    const params = useParams();
    const { success, confirm, error } = SwalAlert();
    const audit = getAuditContext();
    const [snedForApprovalModelOpen, setSnedForApprovalModelOpen] = useState(false)
    const { taskListDetailsData, fetchTaskListDetails, fetchAdmissionBoardCommitteeList } = useContext(MembershipTaskListContext);

    const [sendAdmissionBoardApproval, {
        isLoading: sendAdmissionBoardApprovalIsLoading,
        data: sendAdmissionBoardApprovalData,
        isSuccess: sendAdmissionBoardApprovalSuccess
    }] = useSendAdmissionBoardApprovalMutation();

    useEffect(() => {
        if (!sendAdmissionBoardApprovalIsLoading && sendAdmissionBoardApprovalData && sendAdmissionBoardApprovalSuccess) {
            openModal();
            success("Application is send successfully.", "Success")
            let request = {
                ipasMemberId: Number(decryptAESID(params.memberId)),
                taskListId: Number(decryptAESID(params.taskListId)),
            }
            fetchTaskListDetails(request);
            fetchAdmissionBoardCommitteeList(request)
        }
    }, [sendAdmissionBoardApprovalIsLoading, sendAdmissionBoardApprovalData, sendAdmissionBoardApprovalSuccess]);

    const [submitAdmissionBoardApproval, {
        isLoading: submitAdmissionBoardApprovalIsLoading,
        data: submitAdmissionBoardApprovalData,
        isSuccess: submitAdmissionBoardApprovalSuccess
    }] = useSubmitAdmissionBoardApprovalMutation();

    useEffect(() => {
        if (taskListDetailsData) {
            let updateForm = setAdmissionCommitteeInitials(taskListDetailsData, admissionCommitteForm);
            setAdmissionCommitteFormForm({ ...updateForm });
        }
    }, [taskListDetailsData]);

    useEffect(() => {
        if (!submitAdmissionBoardApprovalIsLoading && submitAdmissionBoardApprovalData && submitAdmissionBoardApprovalSuccess) {
            success("Application is submitted successfully.", "Success")
            let request = {
                ipasMemberId: Number(decryptAESID(params.memberId)),
                taskListId: Number(decryptAESID(params.taskListId)),
            }
            fetchTaskListDetails(request);
        }
    }, [submitAdmissionBoardApprovalIsLoading, submitAdmissionBoardApprovalData, submitAdmissionBoardApprovalSuccess]);

    useEffect(() => {
        if (taskListDetailsData && fetchTaskListDetails) {
            setAdmissionCommitteFormForm(prevForm => {
                if (!prevForm || !prevForm.section) return prevForm;

                const updatedConfig = {
                    ...prevForm,
                    section: prevForm.section.map(section => ({
                        ...section,
                        fields: section.fields.map(fields => {
                            if (fields?.fieldSetting?.formControltemplate?.props?.actionName === "SEND_FOR_APPROVAL") {
                                const shouldDisable = taskListDetailsData?.applicationStatusId === 10;
                                return {
                                    ...fields,
                                    fieldSetting: {
                                        ...fields.fieldSetting,
                                        isTemplateBtnDisable: shouldDisable,
                                    },
                                };
                            }
                            return fields;
                        }),
                    })),
                };

                return updatedConfig;
            });
        }
    }, [taskListDetailsData, fetchTaskListDetails])

    const handleActionChange = (action, data, formData) => {
        console.log({ taskListDetailsData });
        const { applicationStatusId } = taskListDetailsData;
        if (taskListDetailsData?.isAdmissionBoardSubmittedByAdmin && applicationStatusId != ApplicationStatus.ClarificationSubmitted) {
            return; // if alrady submitted then not allow
        }
        else if (applicationStatusId === ApplicationStatus.SendClarification) {
            return error("Clarification is not submitted yet.", "Validation Error")
        }
        else if (action === "SEND_FOR_APPROVAL") {
            if (taskListDetailsData?.applicationStatusId === 10) {
                return;
            }
            else if (applicationStatusId === ApplicationStatus.PendingApplicationFeeApproval) {
                return error("Pending application fee approval.", "Validation Error")
            }
            else if (applicationStatusId === ApplicationStatus.PendingAdmissionCommittee) {
                return error("Applications already send for approval.", "Validation Error")
            }
            else if (formData) {
                if (!formData.comments || formData.comments.trim() === "" || !formData.deadline || String(formData.deadline).trim() === "") {
                    error("Please enter required fields.", "Validation Error");
                    return;
                }
                openModal();
                let request = {
                    ApplicationTaskListId: Number(decryptAESID(params.taskListId)),
                    IPASMemberId: Number(decryptAESID(params.memberId)),
                    AdmissionBoardSecretarialSummary: formData.comments,
                    AdmissionBoardDeadline: formData.deadline,
                    CreatedBy: audit.adminUserId,
                    CreatedAppId: audit.appId
                }
                sendAdmissionBoardApproval(request);
            }
        }
        else if (action === "SUBMIT") {
            if (taskListDetailsData?.applicationStatusId === 10) {
                return;
            }
            if (applicationStatusId !== ApplicationStatus.PendingAdmissionCommittee && applicationStatusId !== ApplicationStatus.ClarificationSubmitted) {
                return error("Pending Admission Committee send for approval.", "Validation Error")
            }
            else if (formData) {
                if (!formData.additionalNotes || formData.additionalNotes.trim() === "" || !formData.admissionCommitteeDate || String(formData.admissionCommitteeDate).trim() === "") {
                    error("Please enter required fields.", "Validation Error");
                    return;
                }
                let request = {
                    TaskListId: Number(decryptAESID(params.taskListId)),
                    IPASMemberId: Number(decryptAESID(params.memberId)),
                    AdditionalNotes: formData.additionalNotes,
                    AdmissionCommitteeDate: formData.admissionCommitteeDate,
                    CreatedBy: audit.adminUserId,
                    CreatedAppId: audit.appId
                }
                submitAdmissionBoardApproval(request);
            }
        }
    }

    const openModal = () => {
        setSnedForApprovalModelOpen(!snedForApprovalModelOpen);
    }

    return (

        <div>
            <FormCreator
                config={admissionCommitteForm}
                ref={admissionCommitteRef}
                key={false}
                onActionChange={handleActionChange}
            />
            {snedForApprovalModelOpen && <CenterModel
                modelTitle={'Sending application for approval.'}
                show={"test"}
                className='custom-modal-50'
            //handleClose={openModal}
            //onUpload={openModal}
            >
                <DataLoader />
                {/* <Buttons
                    buttonText="Reject Application"
                    buttonTypeClassName="theme-btn btn mt-5"
                 isLoading={rejectMemberApplicationIsLoading}
                 onClick={handleReject}
                /> */}
            </CenterModel>
            }
        </div>
    )
}

export default AdmissionBoardApprovalForm